/**
 * Solandia typed API client.
 *
 * `SolandiaApi` exposes one method per REST endpoint defined in
 * docs/API-CONTRACT.md, returning the typed entities from ./types.ts. It is
 * transport-agnostic: it talks through an injected `FetchAdapter`, so the same
 * surface works against
 *   - the real Azure Functions backend (`createHttpAdapter`), and
 *   - a zero-backend `createLocalStorageAdapter()` so the web app can run today.
 *
 * No global `fetch` reference is captured at module load; the adapter is always
 * supplied by the caller (defaulting to the browser's fetch only at call time).
 */
import type {
  AddShoppingItemInput,
  Agreement,
  AppendMessageInput,
  AuditEntry,
  CalendarEvent,
  Child,
  Contact,
  CreateCalendarEventInput,
  CreateChildInput,
  CreateContactInput,
  CreateDocumentInput,
  CreateExpenseInput,
  CreateFamilyInput,
  CreateShoppingListInput,
  CustodySchedule,
  Document as SolandiaDocument,
  DocumentUploadTicket,
  Expense,
  Family,
  FamilyMode,
  HealthRecord,
  Id,
  InviteMemberInput,
  ListResponse,
  MeResponse,
  Membership,
  Message,
  ProposeAgreementInput,
  ShoppingItem,
  ShoppingList,
  UpdateCalendarEventInput,
  UpdateChildInput,
  UpdateContactInput,
  UpdateExpenseInput,
  UpdateFamilyInput,
  UpdateHealthRecordInput,
  UpdateMemberInput,
  UpdateProfileInput,
  UpdateShoppingItemInput,
  UpsertCustodyScheduleInput,
  User,
} from './types.js';

// ---------------------------------------------------------------------------
// Transport abstraction
// ---------------------------------------------------------------------------

export type HttpMethod = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';

export interface ApiRequest {
  method: HttpMethod;
  /** Path relative to the API root, e.g. "/families/abc/children". */
  path: string;
  /** Optional query params. Undefined/null values are dropped. */
  query?: Record<string, string | number | boolean | null | undefined>;
  /** JSON-serializable request body. */
  body?: unknown;
}

/**
 * A transport. Implementations turn an ApiRequest into a parsed JSON response.
 * They MUST throw `ApiClientError` on non-2xx responses.
 */
export interface FetchAdapter {
  request<T>(req: ApiRequest): Promise<T>;
}

/** Thrown by adapters on a non-2xx response or a transport failure. */
export class ApiClientError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: Record<string, string[]>,
  ) {
    super(message);
    this.name = 'ApiClientError';
  }
}

// ---------------------------------------------------------------------------
// HTTP adapter (real backend)
// ---------------------------------------------------------------------------

export interface HttpAdapterOptions {
  /** API root. Defaults to "/api". */
  baseUrl?: string;
  /** Injected fetch (e.g. for SSR/tests). Defaults to global fetch at call time. */
  fetchImpl?: typeof fetch;
  /** Extra headers applied to every request (e.g. an auth token in non-SWA hosting). */
  headers?: Record<string, string>;
  /** Per-request timeout in ms (via AbortController). Default 65000 — tolerant of serverless cold-starts. */
  timeoutMs?: number;
  /** Retries on network error / timeout / 503 (serverless + SQL-resume resilience). Default 0. */
  retries?: number;
}

function buildUrl(
  baseUrl: string,
  path: string,
  query?: ApiRequest['query'],
): string {
  const base = baseUrl.replace(/\/$/, '');
  const p = path.startsWith('/') ? path : `/${path}`;
  let url = `${base}${p}`;
  if (query) {
    const params = new URLSearchParams();
    for (const [k, v] of Object.entries(query)) {
      if (v !== undefined && v !== null) params.set(k, String(v));
    }
    const qs = params.toString();
    if (qs) url += `?${qs}`;
  }
  return url;
}

/**
 * Adapter for the real backend. In Azure Static Web Apps the identity is
 * injected by the platform via the `x-ms-client-principal` header, so the
 * client itself sends no token; cookies/headers are handled by SWA.
 */
export function createHttpAdapter(options: HttpAdapterOptions = {}): FetchAdapter {
  const baseUrl = options.baseUrl ?? '/api';
  const timeoutMs = options.timeoutMs ?? 65_000;
  const maxRetries = Math.max(0, options.retries ?? 0);
  return {
    async request<T>(req: ApiRequest): Promise<T> {
      const doFetch = options.fetchImpl ?? globalThis.fetch;
      if (!doFetch) {
        throw new ApiClientError(0, 'no_fetch', 'No fetch implementation available.');
      }
      const url = buildUrl(baseUrl, req.path, req.query);
      const init: RequestInit = {
        method: req.method,
        headers: {
          Accept: 'application/json',
          ...(req.body !== undefined ? { 'Content-Type': 'application/json' } : {}),
          ...options.headers,
        },
        ...(req.body !== undefined ? { body: JSON.stringify(req.body) } : {}),
      };

      // Retry transient failures — the signature of a serverless backend cold-
      // starting or Azure SQL serverless resuming from auto-pause.
      let attempt = 0;
      for (;;) {
        const controller =
          typeof AbortController !== 'undefined' ? new AbortController() : undefined;
        const timer = controller
          ? setTimeout(() => controller.abort(), timeoutMs)
          : undefined;
        let res: Response;
        try {
          res = await doFetch(
            url,
            controller ? { ...init, signal: controller.signal } : init,
          );
        } catch (err) {
          if (timer) clearTimeout(timer);
          const timedOut = err instanceof Error && err.name === 'AbortError';
          if (attempt < maxRetries) {
            attempt += 1;
            await delay(backoffMs(attempt));
            continue;
          }
          throw new ApiClientError(
            0,
            timedOut ? 'timeout' : 'network_error',
            timedOut
              ? 'Request timed out.'
              : err instanceof Error
                ? err.message
                : 'Network request failed.',
          );
        }
        if (timer) clearTimeout(timer);

        // 503/504 right after idle = still warming up → retry.
        if ((res.status === 503 || res.status === 504) && attempt < maxRetries) {
          attempt += 1;
          await delay(backoffMs(attempt));
          continue;
        }

        if (res.status === 204) return undefined as T;

        const text = await res.text();
        const json = text ? safeJsonParse(text) : undefined;

        if (!res.ok) {
          const errBody = json as { error?: { code?: string; message?: string; details?: Record<string, string[]> } } | undefined;
          throw new ApiClientError(
            res.status,
            errBody?.error?.code ?? 'http_error',
            errBody?.error?.message ?? res.statusText ?? 'Request failed.',
            errBody?.error?.details,
          );
        }
        return json as T;
      }
    },
  };
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/** Exponential backoff, capped at 8s. */
function backoffMs(attempt: number): number {
  return Math.min(1000 * 2 ** (attempt - 1), 8000);
}

function safeJsonParse(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return undefined;
  }
}

// ---------------------------------------------------------------------------
// SolandiaApi — typed surface over the transport
// ---------------------------------------------------------------------------

export class SolandiaApi {
  constructor(private readonly adapter: FetchAdapter) {}

  // -- auth -----------------------------------------------------------------

  /** GET /auth/me — current user + their memberships/families. */
  me(): Promise<MeResponse> {
    return this.adapter.request({ method: 'GET', path: '/auth/me' });
  }

  /** PATCH /auth/me — update the signed-in user's own profile (display name). */
  updateMe(input: UpdateProfileInput): Promise<User> {
    return this.adapter.request({ method: 'PATCH', path: '/auth/me', body: input });
  }

  // -- families -------------------------------------------------------------

  listFamilies(): Promise<ListResponse<Family>> {
    return this.adapter.request({ method: 'GET', path: '/families' });
  }

  getFamily(familyId: Id): Promise<Family> {
    return this.adapter.request({ method: 'GET', path: `/families/${familyId}` });
  }

  createFamily(input: CreateFamilyInput): Promise<Family> {
    return this.adapter.request({ method: 'POST', path: '/families', body: input });
  }

  updateFamily(familyId: Id, input: UpdateFamilyInput): Promise<Family> {
    return this.adapter.request({ method: 'PATCH', path: `/families/${familyId}`, body: input });
  }

  deleteFamily(familyId: Id): Promise<void> {
    return this.adapter.request({ method: 'DELETE', path: `/families/${familyId}` });
  }

  /**
   * POST /families/:id/mode — switch the family's operating mode (parents only).
   * A deliberate, audit-logged state transition; the server stamps
   * `modeChangedAt`. Returns the updated family.
   */
  setFamilyMode(familyId: Id, mode: FamilyMode): Promise<Family> {
    return this.adapter.request({
      method: 'POST',
      path: `/families/${familyId}/mode`,
      body: { mode },
    });
  }

  // -- members --------------------------------------------------------------

  listMembers(familyId: Id): Promise<ListResponse<Membership>> {
    return this.adapter.request({ method: 'GET', path: `/families/${familyId}/members` });
  }

  inviteMember(familyId: Id, input: InviteMemberInput): Promise<Membership> {
    return this.adapter.request({
      method: 'POST',
      path: `/families/${familyId}/members`,
      body: input,
    });
  }

  /** Accept a pending invite (the invited user calls this). */
  acceptMembership(membershipId: Id): Promise<Membership> {
    return this.adapter.request({
      method: 'POST',
      path: `/memberships/${membershipId}/accept`,
    });
  }

  updateMember(familyId: Id, membershipId: Id, input: UpdateMemberInput): Promise<Membership> {
    return this.adapter.request({
      method: 'PATCH',
      path: `/families/${familyId}/members/${membershipId}`,
      body: input,
    });
  }

  removeMember(familyId: Id, membershipId: Id): Promise<void> {
    return this.adapter.request({
      method: 'DELETE',
      path: `/families/${familyId}/members/${membershipId}`,
    });
  }

  // -- children -------------------------------------------------------------

  listChildren(familyId: Id): Promise<ListResponse<Child>> {
    return this.adapter.request({ method: 'GET', path: `/families/${familyId}/children` });
  }

  getChild(familyId: Id, childId: Id): Promise<Child> {
    return this.adapter.request({
      method: 'GET',
      path: `/families/${familyId}/children/${childId}`,
    });
  }

  createChild(familyId: Id, input: CreateChildInput): Promise<Child> {
    return this.adapter.request({
      method: 'POST',
      path: `/families/${familyId}/children`,
      body: input,
    });
  }

  updateChild(familyId: Id, childId: Id, input: UpdateChildInput): Promise<Child> {
    return this.adapter.request({
      method: 'PATCH',
      path: `/families/${familyId}/children/${childId}`,
      body: input,
    });
  }

  deleteChild(familyId: Id, childId: Id): Promise<void> {
    return this.adapter.request({
      method: 'DELETE',
      path: `/families/${familyId}/children/${childId}`,
    });
  }

  // -- contacts -------------------------------------------------------------

  listContacts(familyId: Id, childId: Id): Promise<ListResponse<Contact>> {
    return this.adapter.request({
      method: 'GET',
      path: `/families/${familyId}/children/${childId}/contacts`,
    });
  }

  createContact(familyId: Id, childId: Id, input: CreateContactInput): Promise<Contact> {
    return this.adapter.request({
      method: 'POST',
      path: `/families/${familyId}/children/${childId}/contacts`,
      body: input,
    });
  }

  updateContact(
    familyId: Id,
    childId: Id,
    contactId: Id,
    input: UpdateContactInput,
  ): Promise<Contact> {
    return this.adapter.request({
      method: 'PATCH',
      path: `/families/${familyId}/children/${childId}/contacts/${contactId}`,
      body: input,
    });
  }

  deleteContact(familyId: Id, childId: Id, contactId: Id): Promise<void> {
    return this.adapter.request({
      method: 'DELETE',
      path: `/families/${familyId}/children/${childId}/contacts/${contactId}`,
    });
  }

  // -- health (GDPR Art. 9) -------------------------------------------------

  getHealthRecord(familyId: Id, childId: Id): Promise<HealthRecord> {
    return this.adapter.request({
      method: 'GET',
      path: `/families/${familyId}/children/${childId}/health`,
    });
  }

  updateHealthRecord(
    familyId: Id,
    childId: Id,
    input: UpdateHealthRecordInput,
  ): Promise<HealthRecord> {
    return this.adapter.request({
      method: 'PUT',
      path: `/families/${familyId}/children/${childId}/health`,
      body: input,
    });
  }

  // -- calendar events ------------------------------------------------------

  listEvents(
    familyId: Id,
    childId: Id,
    range?: { from?: string; to?: string },
  ): Promise<ListResponse<CalendarEvent>> {
    return this.adapter.request({
      method: 'GET',
      path: `/families/${familyId}/children/${childId}/events`,
      query: { from: range?.from, to: range?.to },
    });
  }

  createEvent(
    familyId: Id,
    childId: Id,
    input: CreateCalendarEventInput,
  ): Promise<CalendarEvent> {
    return this.adapter.request({
      method: 'POST',
      path: `/families/${familyId}/children/${childId}/events`,
      body: input,
    });
  }

  updateEvent(
    familyId: Id,
    childId: Id,
    eventId: Id,
    input: UpdateCalendarEventInput,
  ): Promise<CalendarEvent> {
    return this.adapter.request({
      method: 'PATCH',
      path: `/families/${familyId}/children/${childId}/events/${eventId}`,
      body: input,
    });
  }

  deleteEvent(familyId: Id, childId: Id, eventId: Id): Promise<void> {
    return this.adapter.request({
      method: 'DELETE',
      path: `/families/${familyId}/children/${childId}/events/${eventId}`,
    });
  }

  // -- custody schedule -----------------------------------------------------

  getCustodySchedule(familyId: Id, childId: Id): Promise<CustodySchedule | null> {
    return this.adapter.request({
      method: 'GET',
      path: `/families/${familyId}/children/${childId}/custody-schedule`,
    });
  }

  upsertCustodySchedule(
    familyId: Id,
    childId: Id,
    input: UpsertCustodyScheduleInput,
  ): Promise<CustodySchedule> {
    return this.adapter.request({
      method: 'PUT',
      path: `/families/${familyId}/children/${childId}/custody-schedule`,
      body: input,
    });
  }

  // -- documents ------------------------------------------------------------

  listDocuments(familyId: Id, childId: Id): Promise<ListResponse<SolandiaDocument>> {
    return this.adapter.request({
      method: 'GET',
      path: `/families/${familyId}/children/${childId}/documents`,
    });
  }

  /**
   * Request a SAS upload ticket. The client then PUTs the file bytes to
   * `ticket.uploadUrl` and the document is listed once the upload completes.
   */
  createDocumentUpload(
    familyId: Id,
    childId: Id,
    input: CreateDocumentInput,
  ): Promise<DocumentUploadTicket> {
    return this.adapter.request({
      method: 'POST',
      path: `/families/${familyId}/children/${childId}/documents`,
      body: input,
    });
  }

  deleteDocument(familyId: Id, childId: Id, documentId: Id): Promise<void> {
    return this.adapter.request({
      method: 'DELETE',
      path: `/families/${familyId}/children/${childId}/documents/${documentId}`,
    });
  }

  // -- messages (append-only) ----------------------------------------------

  listMessages(
    familyId: Id,
    page?: { cursor?: string; limit?: number },
  ): Promise<ListResponse<Message>> {
    return this.adapter.request({
      method: 'GET',
      path: `/families/${familyId}/messages`,
      query: { cursor: page?.cursor, limit: page?.limit },
    });
  }

  /** Append an immutable message. There is intentionally no edit/delete. */
  appendMessage(familyId: Id, input: AppendMessageInput): Promise<Message> {
    return this.adapter.request({
      method: 'POST',
      path: `/families/${familyId}/messages`,
      body: input,
    });
  }

  // -- expenses -------------------------------------------------------------

  listExpenses(familyId: Id): Promise<ListResponse<Expense>> {
    return this.adapter.request({ method: 'GET', path: `/families/${familyId}/expenses` });
  }

  createExpense(familyId: Id, input: CreateExpenseInput): Promise<Expense> {
    return this.adapter.request({
      method: 'POST',
      path: `/families/${familyId}/expenses`,
      body: input,
    });
  }

  updateExpense(familyId: Id, expenseId: Id, input: UpdateExpenseInput): Promise<Expense> {
    return this.adapter.request({
      method: 'PATCH',
      path: `/families/${familyId}/expenses/${expenseId}`,
      body: input,
    });
  }

  deleteExpense(familyId: Id, expenseId: Id): Promise<void> {
    return this.adapter.request({
      method: 'DELETE',
      path: `/families/${familyId}/expenses/${expenseId}`,
    });
  }

  /** Mark an expense settled (server stamps settledAt). */
  settleExpense(familyId: Id, expenseId: Id): Promise<Expense> {
    return this.adapter.request({
      method: 'POST',
      path: `/families/${familyId}/expenses/${expenseId}/settle`,
    });
  }

  // -- shopping (family-wide lists + items) ---------------------------------

  /** GET /families/:id/shopping-lists — all lists in the family. */
  listShoppingLists(familyId: Id): Promise<ListResponse<ShoppingList>> {
    return this.adapter.request({
      method: 'GET',
      path: `/families/${familyId}/shopping-lists`,
    });
  }

  /** POST /families/:id/shopping-lists — create a named list. */
  createShoppingList(familyId: Id, name: string): Promise<ShoppingList> {
    const input: CreateShoppingListInput = { name };
    return this.adapter.request({
      method: 'POST',
      path: `/families/${familyId}/shopping-lists`,
      body: input,
    });
  }

  /** DELETE /shopping-lists/:id — delete a list and its items. */
  deleteShoppingList(listId: Id): Promise<void> {
    return this.adapter.request({
      method: 'DELETE',
      path: `/shopping-lists/${listId}`,
    });
  }

  /** GET /shopping-lists/:id/items — all items on a list. */
  listShoppingItems(listId: Id): Promise<ListResponse<ShoppingItem>> {
    return this.adapter.request({
      method: 'GET',
      path: `/shopping-lists/${listId}/items`,
    });
  }

  /** POST /shopping-lists/:id/items — add an item to a list. */
  addShoppingItem(listId: Id, input: AddShoppingItemInput): Promise<ShoppingItem> {
    return this.adapter.request({
      method: 'POST',
      path: `/shopping-lists/${listId}/items`,
      body: input,
    });
  }

  /**
   * PATCH /shopping-items/:id — update an item. Toggling `checked` stamps/clears
   * `checkedAt` server-side.
   */
  updateShoppingItem(itemId: Id, patch: UpdateShoppingItemInput): Promise<ShoppingItem> {
    return this.adapter.request({
      method: 'PATCH',
      path: `/shopping-items/${itemId}`,
      body: patch,
    });
  }

  /** DELETE /shopping-items/:id — remove a single item. */
  deleteShoppingItem(itemId: Id): Promise<void> {
    return this.adapter.request({
      method: 'DELETE',
      path: `/shopping-items/${itemId}`,
    });
  }

  /**
   * POST /shopping-lists/:id/clear-bought — delete every checked-off item on
   * the list. Returns the surviving (still-outstanding) items.
   */
  clearBoughtItems(listId: Id): Promise<ListResponse<ShoppingItem>> {
    return this.adapter.request({
      method: 'POST',
      path: `/shopping-lists/${listId}/clear-bought`,
    });
  }

  // -- agreements (parents-only; accepted = locked record) ------------------

  listAgreements(familyId: Id): Promise<ListResponse<Agreement>> {
    return this.adapter.request({ method: 'GET', path: `/families/${familyId}/agreements` });
  }

  /** Propose a new agreement (status `proposed`). Proposer = caller. */
  proposeAgreement(familyId: Id, input: ProposeAgreementInput): Promise<Agreement> {
    return this.adapter.request({
      method: 'POST',
      path: `/families/${familyId}/agreements`,
      body: input,
    });
  }

  /** Accept a proposed agreement — only the OTHER active parent may. Locks it. */
  acceptAgreement(agreementId: Id): Promise<Agreement> {
    return this.adapter.request({
      method: 'POST',
      path: `/agreements/${agreementId}/accept`,
    });
  }

  /** Decline a proposed agreement — only the OTHER active parent may. Locks it. */
  declineAgreement(agreementId: Id): Promise<Agreement> {
    return this.adapter.request({
      method: 'POST',
      path: `/agreements/${agreementId}/decline`,
    });
  }

  /** Withdraw a proposed agreement — only the proposer may. Locks it. */
  withdrawAgreement(agreementId: Id): Promise<Agreement> {
    return this.adapter.request({
      method: 'POST',
      path: `/agreements/${agreementId}/withdraw`,
    });
  }

  // -- audit (read-only) ----------------------------------------------------

  listAudit(
    familyId: Id,
    page?: { cursor?: string; limit?: number },
  ): Promise<ListResponse<AuditEntry>> {
    return this.adapter.request({
      method: 'GET',
      path: `/families/${familyId}/audit`,
      query: { cursor: page?.cursor, limit: page?.limit },
    });
  }
}

/**
 * Convenience constructor for the common case: build a SolandiaApi backed by the
 * real HTTP backend.
 */
export function createSolandiaApi(options?: HttpAdapterOptions): SolandiaApi {
  return new SolandiaApi(createHttpAdapter(options));
}

// ---------------------------------------------------------------------------
// localStorage mock adapter — run the web app with NO backend
// ---------------------------------------------------------------------------

/** Minimal storage interface (matches the slice of Web Storage we use). */
export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export interface LocalStorageAdapterOptions {
  /** Storage backend. Defaults to `globalThis.localStorage`. */
  storage?: StorageLike;
  /** Key prefix for stored collections. Defaults to "solandia". */
  namespace?: string;
  /** ID generator. Defaults to crypto.randomUUID. */
  generateId?: () => string;
  /** Clock injector. Defaults to `() => new Date()`. */
  now?: () => Date;
  /** Identity of the "current user" the mock acts as. */
  currentUser?: { id: string; email: string; displayName: string };
  /** Seed data applied on first use (when the namespace is empty). */
  seed?: (db: MockDb) => void;
}

/** In-memory representation of every collection, persisted to storage as JSON. */
export interface MockDb {
  users: Record<string, unknown>;
  families: Family[];
  memberships: Membership[];
  children: Child[];
  contacts: Contact[];
  health: HealthRecord[];
  events: CalendarEvent[];
  custody: CustodySchedule[];
  documents: SolandiaDocument[];
  messages: Message[];
  expenses: Expense[];
  shoppingLists: ShoppingList[];
  shoppingItems: ShoppingItem[];
  agreements: Agreement[];
  audit: AuditEntry[];
}

function emptyDb(): MockDb {
  return {
    users: {},
    families: [],
    memberships: [],
    children: [],
    contacts: [],
    health: [],
    events: [],
    custody: [],
    documents: [],
    messages: [],
    expenses: [],
    shoppingLists: [],
    shoppingItems: [],
    agreements: [],
    audit: [],
  };
}

/**
 * A fully in-process FetchAdapter that stores entities in localStorage. It lets
 * the web app exercise every screen before the API exists. It enforces the
 * family scoping shape (paths carry familyId) but is NOT a security boundary —
 * it is a developer convenience only.
 */
export function createLocalStorageAdapter(
  options: LocalStorageAdapterOptions = {},
): FetchAdapter {
  const storage = options.storage ?? (globalThis as { localStorage?: StorageLike }).localStorage;
  if (!storage) {
    throw new Error('createLocalStorageAdapter: no Storage available (pass options.storage).');
  }
  const ns = options.namespace ?? 'solandia';
  const key = `${ns}:db`;
  const genId =
    options.generateId ?? (() => globalThis.crypto?.randomUUID?.() ?? fallbackId());
  const now = options.now ?? (() => new Date());
  const me = { ...(options.currentUser ?? {
    id: 'local-user',
    email: 'local@solandia.com',
    displayName: 'Local Parent',
  }) };

  function load(): MockDb {
    const raw = storage!.getItem(key);
    if (raw) {
      try {
        // Merge over a fresh shell so a pre-pivot persisted DB (missing the
        // shopping collections) still yields every expected array. Families
        // missing `mode`/`modeChangedAt` are backfilled to the family-mode
        // default — mirrors the schema's deserialization default.
        const parsed = JSON.parse(raw) as Partial<MockDb>;
        const db: MockDb = { ...emptyDb(), ...parsed };
        db.families = db.families.map((f) => ({
          ...f,
          mode: f.mode ?? 'family',
          modeChangedAt: f.modeChangedAt ?? null,
        }));
        return db;
      } catch {
        // fall through to fresh db
      }
    }
    const db = emptyDb();
    options.seed?.(db);
    storage!.setItem(key, JSON.stringify(db));
    return db;
  }

  function save(db: MockDb): void {
    storage!.setItem(key, JSON.stringify(db));
  }

  function nowIso(): string {
    return now().toISOString();
  }

  function notFound(): never {
    throw new ApiClientError(404, 'not_found', 'Resource not found (mock).');
  }

  function list<T>(items: T[]): ListResponse<T> {
    return { items, nextCursor: null };
  }

  /** Parse the path into segments, ignoring query strings. */
  function segments(path: string): string[] {
    return path.split('?')[0]!.split('/').filter(Boolean);
  }

  function audit(
    db: MockDb,
    familyId: string,
    action: string,
    entityType: string,
    entityId: string | null,
    summary: string,
  ): void {
    db.audit.push({
      id: genId(),
      familyId,
      actorId: me.id,
      action,
      entityType,
      entityId,
      summary,
      createdAt: nowIso(),
    });
  }

  return {
    async request<T>(req: ApiRequest): Promise<T> {
      const db = load();
      const seg = segments(req.path);
      const body = (req.body ?? {}) as Record<string, unknown>;
      const result = route<T>({ db, seg, method: req.method, body, query: req.query });
      save(db);
      return result;

      // -- inner router (closes over helpers) --------------------------------
      function route<R>(ctx: {
        db: MockDb;
        seg: string[];
        method: HttpMethod;
        body: Record<string, unknown>;
        query: ApiRequest['query'];
      }): R {
        const { db, seg, method } = ctx;
        const m = `${method} /${seg.join('/')}`;

        // GET /auth/me
        if (seg[0] === 'auth' && seg[1] === 'me' && method === 'GET') {
          const memberships = db.memberships
            .filter((mb) => mb.userId === me.id)
            .map((mb) => ({
              membership: mb,
              family: db.families.find((f) => f.id === mb.familyId)!,
            }))
            .filter((x) => x.family);
          const res: MeResponse = {
            user: { ...me, authProvider: 'email', createdAt: nowIso() },
            memberships,
          };
          return res as R;
        }

        // PATCH /auth/me — update the current user's own profile.
        if (seg[0] === 'auth' && seg[1] === 'me' && method === 'PATCH') {
          if (ctx.body.displayName !== undefined) {
            me.displayName = String(ctx.body.displayName).trim();
          }
          const updated: User = {
            ...me,
            authProvider: 'email',
            createdAt: nowIso(),
          };
          return updated as R;
        }

        if (seg[0] === 'families') {
          // /families
          if (seg.length === 1) {
            if (method === 'GET') return list(db.families) as R;
            if (method === 'POST') {
              const fam: Family = {
                id: genId(),
                name: String(ctx.body.name ?? 'Naša rodina'),
                mode: 'family',
                modeChangedAt: null,
                createdAt: nowIso(),
              };
              db.families.push(fam);
              db.memberships.push({
                id: genId(),
                familyId: fam.id,
                userId: me.id,
                role: 'parent',
                permissions: [],
                invitedById: null,
                status: 'active',
                createdAt: nowIso(),
              });
              audit(db, fam.id, 'create', 'Family', fam.id, `Vytvorená rodina ${fam.name}`);
              return fam as R;
            }
          }

          const familyId = seg[1]!;
          const sub = seg[2];

          // /families/:id
          if (seg.length === 2) {
            const fam = db.families.find((f) => f.id === familyId);
            if (!fam) notFound();
            if (method === 'GET') return fam as R;
            if (method === 'PATCH') {
              if (ctx.body.name !== undefined) fam.name = String(ctx.body.name);
              audit(db, fam.id, 'update', 'Family', fam.id, 'Upravená rodina');
              return fam as R;
            }
            if (method === 'DELETE') {
              db.families = db.families.filter((f) => f.id !== familyId);
              return undefined as R;
            }
          }

          // /families/:id/mode — switch operating mode (parents only IRL).
          if (sub === 'mode' && seg.length === 3 && method === 'POST') {
            const fam = db.families.find((f) => f.id === familyId);
            if (!fam) notFound();
            const nextMode = String(ctx.body.mode);
            if (nextMode !== 'family' && nextMode !== 'separated') {
              throw new ApiClientError(400, 'validation_error', 'Invalid mode (mock).');
            }
            if (fam.mode !== nextMode) {
              fam.mode = nextMode;
              fam.modeChangedAt = nowIso();
              audit(db, fam.id, 'set-mode', 'Family', fam.id, `Režim rodiny: ${nextMode}`);
            }
            return fam as R;
          }

          // /families/:id/children ...
          if (sub === 'children') return childRoutes(ctx, familyId) as R;
          if (sub === 'members') return memberRoutes(ctx, familyId) as R;
          if (sub === 'messages') return messageRoutes(ctx, familyId) as R;
          if (sub === 'expenses') return expenseRoutes(ctx, familyId) as R;
          if (sub === 'shopping-lists') return shoppingListRoutes(ctx, familyId) as R;
          if (sub === 'agreements') return agreementRoutes(ctx, familyId) as R;
          if (sub === 'audit') {
            if (method === 'GET') {
              return list(db.audit.filter((a) => a.familyId === familyId)) as R;
            }
          }
        }

        // /shopping-lists/:id ... (items live under a list)
        if (seg[0] === 'shopping-lists') {
          const listId = seg[1]!;
          const listSub = seg[2];
          const list = db.shoppingLists.find((l) => l.id === listId);

          // /shopping-lists/:id
          if (seg.length === 2) {
            if (!list) notFound();
            if (method === 'DELETE') {
              db.shoppingItems = db.shoppingItems.filter((i) => i.listId !== listId);
              db.shoppingLists = db.shoppingLists.filter((l) => l.id !== listId);
              audit(db, list.familyId, 'delete', 'ShoppingList', list.id, `Zmazaný zoznam ${list.name}`);
              return undefined as R;
            }
          }

          // /shopping-lists/:id/items
          if (listSub === 'items' && seg.length === 3) {
            if (!list) notFound();
            if (method === 'GET') {
              return list2(
                db.shoppingItems
                  .filter((i) => i.listId === listId)
                  .sort((a, b) => a.createdAt.localeCompare(b.createdAt)),
              ) as R;
            }
            if (method === 'POST') {
              const item: ShoppingItem = {
                id: genId(),
                listId,
                name: String(ctx.body.name ?? ''),
                category: String(ctx.body.category ?? ''),
                assigneeId: (ctx.body.assigneeId as string | null) ?? null,
                checked: false,
                checkedAt: null,
                createdById: me.id,
                createdAt: nowIso(),
              };
              db.shoppingItems.push(item);
              return item as R;
            }
          }

          // /shopping-lists/:id/clear-bought
          if (listSub === 'clear-bought' && seg.length === 3 && method === 'POST') {
            if (!list) notFound();
            db.shoppingItems = db.shoppingItems.filter(
              (i) => !(i.listId === listId && i.checked),
            );
            audit(db, list.familyId, 'clear-bought', 'ShoppingList', list.id, 'Vymazané kúpené položky');
            return list2(
              db.shoppingItems
                .filter((i) => i.listId === listId)
                .sort((a, b) => a.createdAt.localeCompare(b.createdAt)),
            ) as R;
          }
        }

        // /shopping-items/:id
        if (seg[0] === 'shopping-items' && seg.length === 2) {
          const itemId = seg[1]!;
          const item = db.shoppingItems.find((i) => i.id === itemId);
          if (!item) notFound();
          if (method === 'PATCH') {
            if (ctx.body.name !== undefined) item.name = String(ctx.body.name);
            if (ctx.body.category !== undefined) item.category = String(ctx.body.category);
            if (ctx.body.assigneeId !== undefined) {
              item.assigneeId = (ctx.body.assigneeId as string | null) ?? null;
            }
            if (ctx.body.checked !== undefined) {
              const next = Boolean(ctx.body.checked);
              if (next !== item.checked) {
                item.checked = next;
                item.checkedAt = next ? nowIso() : null;
              }
            }
            return item as R;
          }
          if (method === 'DELETE') {
            db.shoppingItems = db.shoppingItems.filter((i) => i.id !== itemId);
            return undefined as R;
          }
        }

        // /memberships/:id/accept
        if (seg[0] === 'memberships' && seg[2] === 'accept' && method === 'POST') {
          const mb = db.memberships.find((x) => x.id === seg[1]);
          if (!mb) notFound();
          mb.status = 'active';
          return mb as R;
        }

        // /agreements/:id/{accept|decline|withdraw}
        if (seg[0] === 'agreements' && seg[2] && method === 'POST') {
          const ag = db.agreements.find((x) => x.id === seg[1]);
          if (!ag) notFound();
          if (ag.status !== 'proposed') {
            throw new ApiClientError(409, 'conflict', 'Agreement is locked (mock).');
          }
          const action = seg[2];
          if (action === 'accept') ag.status = 'accepted';
          else if (action === 'decline') ag.status = 'declined';
          else if (action === 'withdraw') ag.status = 'withdrawn';
          else notFound();
          ag.respondedById = me.id;
          ag.respondedAt = nowIso();
          audit(db, ag.familyId, action, 'Agreement', ag.id, `Dohoda ${action}.`);
          return ag as R;
        }

        throw new ApiClientError(404, 'no_route', `Mock: no route for ${m}.`);
      }

      function memberRoutes(
        ctx: { db: MockDb; seg: string[]; method: HttpMethod; body: Record<string, unknown> },
        familyId: string,
      ): unknown {
        const { db, seg, method, body } = ctx;
        const membershipId = seg[3];
        if (!membershipId) {
          if (method === 'GET') {
            return list(db.memberships.filter((x) => x.familyId === familyId));
          }
          if (method === 'POST') {
            const mb: Membership = {
              id: genId(),
              familyId,
              userId: genId(),
              role: (body.role as Membership['role']) ?? 'trusted',
              permissions: (body.permissions as Membership['permissions']) ?? [],
              invitedById: me.id,
              status: 'invited',
              createdAt: nowIso(),
            };
            db.memberships.push(mb);
            audit(db, familyId, 'invite', 'Membership', mb.id, `Pozvánka pre ${String(body.email)}`);
            return mb;
          }
        } else {
          const mb = db.memberships.find((x) => x.id === membershipId);
          if (!mb) notFound();
          if (method === 'PATCH') {
            if (body.role !== undefined) mb.role = body.role as Membership['role'];
            if (body.permissions !== undefined) {
              mb.permissions = body.permissions as Membership['permissions'];
            }
            return mb;
          }
          if (method === 'DELETE') {
            db.memberships = db.memberships.filter((x) => x.id !== membershipId);
            return undefined;
          }
        }
        notFound();
      }

      function childRoutes(
        ctx: { db: MockDb; seg: string[]; method: HttpMethod; body: Record<string, unknown> },
        familyId: string,
      ): unknown {
        const { db, seg, method, body } = ctx;
        const childId = seg[3];

        if (!childId) {
          if (method === 'GET') return list(db.children.filter((c) => c.familyId === familyId));
          if (method === 'POST') {
            const child: Child = {
              id: genId(),
              familyId,
              name: String(body.name ?? ''),
              avatarEmoji: String(body.avatarEmoji ?? '🐥'),
              birthDate: String(body.birthDate ?? '2015-01-01'),
            };
            db.children.push(child);
            audit(db, familyId, 'create', 'Child', child.id, `Pridané dieťa ${child.name}`);
            return child;
          }
        }

        const child = db.children.find((c) => c.id === childId && c.familyId === familyId);
        const resource = seg[4];

        if (childId && !resource) {
          if (!child) notFound();
          if (method === 'GET') return child;
          if (method === 'PATCH') {
            Object.assign(child, pick(body, ['name', 'avatarEmoji', 'birthDate']));
            return child;
          }
          if (method === 'DELETE') {
            db.children = db.children.filter((c) => c.id !== childId);
            return undefined;
          }
        }

        if (!child) notFound();

        // /children/:id/contacts
        if (resource === 'contacts') {
          const contactId = seg[5];
          if (!contactId) {
            if (method === 'GET') return list(db.contacts.filter((c) => c.childId === childId));
            if (method === 'POST') {
              const contact: Contact = {
                id: genId(),
                childId: childId!,
                type: (body.type as Contact['type']) ?? 'other',
                label: String(body.label ?? ''),
                phone: (body.phone as string | null) ?? null,
                address: (body.address as string | null) ?? null,
                note: (body.note as string | null) ?? null,
                createdById: me.id,
                createdAt: nowIso(),
              };
              db.contacts.push(contact);
              return contact;
            }
          } else {
            const contact = db.contacts.find((c) => c.id === contactId);
            if (!contact) notFound();
            if (method === 'PATCH') {
              Object.assign(contact, pick(body, ['type', 'label', 'phone', 'address', 'note']));
              return contact;
            }
            if (method === 'DELETE') {
              db.contacts = db.contacts.filter((c) => c.id !== contactId);
              return undefined;
            }
          }
        }

        // /children/:id/health
        if (resource === 'health') {
          let rec = db.health.find((h) => h.childId === childId);
          if (method === 'GET') {
            if (!rec) {
              rec = {
                id: genId(),
                childId: childId!,
                allergies: null,
                medications: null,
                insurance: null,
                bloodType: null,
                conditions: null,
                notes: null,
                updatedById: me.id,
                updatedAt: nowIso(),
              };
              db.health.push(rec);
            }
            return rec;
          }
          if (method === 'PUT') {
            if (!rec) {
              rec = {
                id: genId(),
                childId: childId!,
                allergies: null,
                medications: null,
                insurance: null,
                bloodType: null,
                conditions: null,
                notes: null,
                updatedById: me.id,
                updatedAt: nowIso(),
              };
              db.health.push(rec);
            }
            Object.assign(
              rec,
              pick(body, ['allergies', 'medications', 'insurance', 'bloodType', 'conditions', 'notes']),
            );
            rec.updatedById = me.id;
            rec.updatedAt = nowIso();
            audit(db, familyId, 'update', 'HealthRecord', rec.id, 'Upravený zdravotný záznam');
            return rec;
          }
        }

        // /children/:id/events
        if (resource === 'events') {
          const eventId = seg[5];
          if (!eventId) {
            if (method === 'GET') return list(db.events.filter((e) => e.childId === childId));
            if (method === 'POST') {
              const ev: CalendarEvent = {
                id: genId(),
                childId: childId!,
                title: String(body.title ?? ''),
                type: (body.type as CalendarEvent['type']) ?? 'other',
                startAt: String(body.startAt ?? nowIso()),
                endAt: String(body.endAt ?? nowIso()),
                allDay: Boolean(body.allDay),
                location: (body.location as string | null) ?? null,
                note: (body.note as string | null) ?? null,
                createdById: me.id,
                createdAt: nowIso(),
              };
              db.events.push(ev);
              return ev;
            }
          } else {
            const ev = db.events.find((e) => e.id === eventId);
            if (!ev) notFound();
            if (method === 'PATCH') {
              Object.assign(
                ev,
                pick(body, ['title', 'type', 'startAt', 'endAt', 'allDay', 'location', 'note']),
              );
              return ev;
            }
            if (method === 'DELETE') {
              db.events = db.events.filter((e) => e.id !== eventId);
              return undefined;
            }
          }
        }

        // /children/:id/custody-schedule
        if (resource === 'custody-schedule') {
          let cs = db.custody.find((c) => c.childId === childId);
          if (method === 'GET') return cs ?? null;
          if (method === 'PUT') {
            if (!cs) {
              cs = {
                id: genId(),
                childId: childId!,
                pattern: (body.pattern as CustodySchedule['pattern']) ?? 'custom',
                startDate: String(body.startDate ?? toDateOnly(now())),
                notes: (body.notes as string | null) ?? null,
              };
              db.custody.push(cs);
            } else {
              Object.assign(cs, pick(body, ['pattern', 'startDate', 'notes']));
            }
            return cs;
          }
        }

        // /children/:id/documents
        if (resource === 'documents') {
          const documentId = seg[5];
          if (!documentId) {
            if (method === 'GET') return list(db.documents.filter((d) => d.childId === childId));
            if (method === 'POST') {
              const doc: SolandiaDocument = {
                id: genId(),
                childId: childId!,
                name: String(body.name ?? ''),
                category: String(body.category ?? 'other'),
                blobPath: `${familyId}/${childId}/${genId()}`,
                mimeType: String(body.mimeType ?? 'application/octet-stream'),
                sizeBytes: Number(body.sizeBytes ?? 0),
                uploadedById: me.id,
                note: (body.note as string | null) ?? null,
                createdAt: nowIso(),
              };
              db.documents.push(doc);
              const ticket: DocumentUploadTicket = {
                document: doc,
                uploadUrl: `mock://upload/${doc.blobPath}`,
                expiresAt: new Date(now().getTime() + 15 * 60_000).toISOString(),
              };
              return ticket;
            }
          } else {
            if (method === 'DELETE') {
              db.documents = db.documents.filter((d) => d.id !== documentId);
              return undefined;
            }
          }
        }

        notFound();
      }

      function messageRoutes(
        ctx: { db: MockDb; method: HttpMethod; body: Record<string, unknown> },
        familyId: string,
      ): unknown {
        const { db, method, body } = ctx;
        if (method === 'GET') {
          return list(
            db.messages
              .filter((mm) => mm.familyId === familyId)
              .sort((a, b) => a.createdAt.localeCompare(b.createdAt)),
          );
        }
        if (method === 'POST') {
          const msg: Message = {
            id: genId(),
            familyId,
            authorId: me.id,
            body: String(body.body ?? ''),
            attachments: (body.attachments as Message['attachments']) ?? [],
            createdAt: nowIso(),
          };
          db.messages.push(msg);
          audit(db, familyId, 'append', 'Message', msg.id, 'Nová správa v denníku');
          return msg;
        }
        notFound();
      }

      function expenseRoutes(
        ctx: { db: MockDb; seg: string[]; method: HttpMethod; body: Record<string, unknown> },
        familyId: string,
      ): unknown {
        const { db, seg, method, body } = ctx;
        const expenseId = seg[3];
        const action = seg[4];

        if (!expenseId) {
          if (method === 'GET') return list(db.expenses.filter((e) => e.familyId === familyId));
          if (method === 'POST') {
            const exp: Expense = {
              id: genId(),
              familyId,
              childId: (body.childId as string | null) ?? null,
              payerId: me.id,
              amountMinor: Number(body.amountMinor ?? 0),
              currency: String(body.currency ?? 'EUR'),
              category: String(body.category ?? 'other'),
              description: (body.description as string | null) ?? null,
              incurredOn: String(body.incurredOn ?? toDateOnly(now())),
              splitPct: typeof body.splitPct === 'number' ? body.splitPct : 50,
              settledAt: null,
              createdAt: nowIso(),
            };
            db.expenses.push(exp);
            audit(db, familyId, 'create', 'Expense', exp.id, `Pridaný náklad ${exp.amountMinor}`);
            return exp;
          }
        } else {
          const exp = db.expenses.find((e) => e.id === expenseId);
          if (!exp) notFound();
          if (action === 'settle' && method === 'POST') {
            exp.settledAt = nowIso();
            audit(db, familyId, 'settle', 'Expense', exp.id, 'Náklad vyrovnaný');
            return exp;
          }
          if (method === 'PATCH') {
            Object.assign(
              exp,
              pick(body, [
                'childId',
                'amountMinor',
                'currency',
                'category',
                'description',
                'incurredOn',
                'splitPct',
              ]),
            );
            return exp;
          }
          if (method === 'DELETE') {
            db.expenses = db.expenses.filter((e) => e.id !== expenseId);
            return undefined;
          }
        }
        notFound();
      }

      // Alias for the generic list envelope, used where a local `list` variable
      // (a ShoppingList) would shadow the `list()` helper.
      function list2<T>(items: T[]): ListResponse<T> {
        return list(items);
      }

      function shoppingListRoutes(
        ctx: { db: MockDb; seg: string[]; method: HttpMethod; body: Record<string, unknown> },
        familyId: string,
      ): unknown {
        const { db, seg, method, body } = ctx;
        // Only the collection root is handled here (/families/:id/shopping-lists);
        // item-level + per-list ops live under the top-level /shopping-lists/:id.
        if (seg.length === 3) {
          if (method === 'GET') {
            return list(db.shoppingLists.filter((l) => l.familyId === familyId));
          }
          if (method === 'POST') {
            const sl: ShoppingList = {
              id: genId(),
              familyId,
              name: String(body.name ?? ''),
              createdAt: nowIso(),
            };
            db.shoppingLists.push(sl);
            audit(db, familyId, 'create', 'ShoppingList', sl.id, `Pridaný zoznam ${sl.name}`);
            return sl;
          }
        }
        notFound();
      }

      function agreementRoutes(
        ctx: { db: MockDb; seg: string[]; method: HttpMethod; body: Record<string, unknown> },
        familyId: string,
      ): unknown {
        const { db, seg, method, body } = ctx;
        const agreementId = seg[3];
        if (!agreementId) {
          if (method === 'GET') {
            return list(
              db.agreements
                .filter((a) => a.familyId === familyId)
                .sort((a, b) => b.createdAt.localeCompare(a.createdAt)),
            );
          }
          if (method === 'POST') {
            const ag: Agreement = {
              id: genId(),
              familyId,
              proposedById: me.id,
              title: String(body.title ?? ''),
              body: String(body.body ?? ''),
              status: 'proposed',
              respondedById: null,
              respondedAt: null,
              createdAt: nowIso(),
            };
            db.agreements.push(ag);
            audit(db, familyId, 'propose', 'Agreement', ag.id, 'Navrhnutá dohoda.');
            return ag;
          }
        }
        notFound();
      }
    },
  };

  function toDateOnly(d: Date): string {
    return d.toISOString().slice(0, 10);
  }
}

/** Pick a subset of defined keys from an object (skips undefined values). */
function pick<T extends Record<string, unknown>>(obj: T, keys: string[]): Partial<T> {
  const out: Partial<T> = {};
  for (const k of keys) {
    if (obj[k] !== undefined) (out as Record<string, unknown>)[k] = obj[k];
  }
  return out;
}

/** Non-crypto fallback id (mock only). */
function fallbackId(): string {
  return 'mock-' + Math.random().toString(36).slice(2) + Date.now().toString(36);
}
